import { Loader } from "@tm/components"
import { getModuleFromUserContext, useUser, useAddToBasketModuleParameters, useOwnListsCompilationsParameters } from "@tm/context-distribution"
import { ErpBundleParams, ErpDataCustomization, PartsBundleParams, QuantitySuggestionMode, UserModuleType } from "@tm/models"
import Morpheus from "@tm/morpheus"
import { PropsWithChildren, Suspense, useMemo } from "react"
import { createContext, useContextSelector } from "use-context-selector"
import { useErpConfig } from "@tm/utils"
import { ArticleFeedbackConfiguration, useArticleFeedbackConfiguration } from "@bundles/parts/components/ListV2/ArticleListConfiguration"

export type GetStyleCustomizationResponse = {
    erpDataCustomization: ErpDataCustomization
}

/** Contains all available configuration options for the article list functionality. Extend if necessary. */

type CarouselConfiguration = {
    // Article list view options
    showDataSupplierLogos: boolean
    viewOptions: {
        purchasePrice: boolean | "extracted"
        compact: boolean | "extracted"
        images: boolean | "extracted"
        quantitySuggestion: boolean | "extracted"
    }

    // Article list functionality
    hasFavoriteArticles: boolean
    feedbackConfiguration: ArticleFeedbackConfiguration

    hideExtendedDeliveryComponent?: boolean
    showWideTeccomButton?: boolean
    articleListTeccomMode: ErpBundleParams["articleListTeccomMode"]

    // Switch Position of EArtNr and HArtNr
    positionChangeEArtNrHArtNr?: boolean

    imsIcon?: string

    // Article Notes
    enableArticleNotes?: boolean

    // Recommended Articles
    enableCrossSelling?: boolean

    // Memo tooltip icon used for additional Information icon in article item
    memoTooltipIcon?: string
    hideAddToBasketButton: boolean
    highlightWatchlistButton?: boolean

    // erp (price) styling
    erpDataCustomization?: ErpDataCustomization

    avoidErpRequestsForcedByDivisionChange?: boolean
}

/**
 * Shall not be used directly outside of this file.
 * Use export `useArticleListConfiguration` instead.
 */
const CarouselConfigurationContext = createContext<CarouselConfiguration | undefined>(undefined)

/** Provides all the necessary article list configuration. */
export function CarouselConfigurationProvider(props: PropsWithChildren<unknown>) {
    const { highlightWatchlistButton, listV2, imsIcon } = Morpheus.getParams<PartsBundleParams>("parts")

    const { userContext } = useUser()
    const { hasFavoriteArticles } = useOwnListsCompilationsParameters()
    const { showDataSupplierLogos = false } = userContext.parameter

    const {
        /** @todo Check if we really need these two parameters anymore --> NEXT-26321 */
        // hidePurchasePriceIfReplacementArticlesAvailable,
        // priceOrder,
        hideExtendedDeliveryComponent,
        showWideTeccomButton,
        articleListTeccomMode,
        memoTooltipIcon,
    } = Morpheus.getParams<ErpBundleParams>("erp")

    const quantitySuggestionMode = useMemo<QuantitySuggestionMode>(
        () => getModuleFromUserContext(userContext, UserModuleType.Catalog)?.quantitySuggestionMode ?? QuantitySuggestionMode.Deactivated,
        [userContext]
    )

    const enableArticleNotes = useMemo<boolean>(() => !!getModuleFromUserContext(userContext, UserModuleType.ArticleNotes), [userContext.modules])

    const enableCrossSelling = useMemo<boolean>(() => !!getModuleFromUserContext(userContext, UserModuleType.CrossSelling), [userContext.modules])

    const viewOptions = useMemo(
        () => ({
            purchasePrice: listV2?.viewOptions?.purchasePrice ?? true,
            compact: listV2?.viewOptions?.compact ?? true,
            images: listV2?.viewOptions?.images ?? true,
            quantitySuggestion: quantitySuggestionMode !== QuantitySuggestionMode.Deactivated,
        }),
        [listV2, quantitySuggestionMode]
    )

    const positionChangeEArtNrHArtNr = userContext?.parameter.positionChangeEArtNrHArtNr

    const { showBasketButton } = useAddToBasketModuleParameters()

    const { styleCustomization } = useErpConfig()

    const erpDataCustomization = useMemo(() => {
        try {
            const json = (styleCustomization && JSON.parse(styleCustomization)) as GetStyleCustomizationResponse | undefined
            return json?.erpDataCustomization
        } catch {
            return undefined
        }
    }, [styleCustomization])

    // eslint-disable-next-line react/jsx-no-constructed-context-values
    const carouselConfiguration: CarouselConfiguration = {
        showDataSupplierLogos,
        viewOptions,
        hasFavoriteArticles,
        feedbackConfiguration: useArticleFeedbackConfiguration(),
        hideExtendedDeliveryComponent,
        showWideTeccomButton,
        articleListTeccomMode,
        positionChangeEArtNrHArtNr,
        imsIcon,
        enableArticleNotes,
        enableCrossSelling,
        memoTooltipIcon,
        hideAddToBasketButton: !showBasketButton,
        highlightWatchlistButton,
        erpDataCustomization,
        avoidErpRequestsForcedByDivisionChange: !!Morpheus.getParams<ErpBundleParams>("erp").erpCacheStaleTimeSeconds,
    }

    return (
        <Suspense fallback={<Loader />}>
            <CarouselConfigurationContext.Provider value={carouselConfiguration}>{props?.children}</CarouselConfigurationContext.Provider>
        </Suspense>
    )
}

export function useCarouselConfigurationContext<TContext extends CarouselConfiguration>(): TContext
export function useCarouselConfigurationContext<TContext extends CarouselConfiguration, TReturnType>(
    selector: (value: TContext) => TReturnType
): TReturnType
export function useCarouselConfigurationContext<TContext extends CarouselConfiguration, TReturnType>(
    selector?: (value: TContext) => TReturnType
): TContext | TReturnType {
    return useContextSelector(CarouselConfigurationContext, (context) => {
        const validatedContext = getValidatedContext(context)

        if (!selector) {
            return validatedContext
        }

        return selector(validatedContext as TContext)
    }) as TContext | TReturnType
}

function getValidatedContext<TContext extends CarouselConfiguration>(context: TContext | undefined): TContext {
    if (!context) {
        throw new Error(`Used ${useCarouselConfigurationContext.name} outside of ${CarouselConfigurationProvider.name}`)
    }

    return context
}
